import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from 'contexts/BookingContext';
import { useAuth } from 'contexts/AuthContext';
import { format } from 'date-fns';
import { useReactToPrint } from 'react-to-print';

const Confirmation = () => {
  const { state, dispatch } = useBooking();
  const { booker } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef();

  const { confirmation } = state;

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  if (!confirmation) {
    navigate('/');
    return null;
  }

  const {
    poReference,
    mainGuest,
    guests,
    roomLines,
    checkIn,
    checkOut,
    totalPrice,
    currency,
    Reservations: mewsReservations,
  } = confirmation;

  const nights = Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));
  const totalRooms = roomLines.reduce((sum, l) => sum + l.quantity, 0);

  const handleNewBooking = () => {
    dispatch({ type: 'RESET' });
    navigate('/');
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Success banner */}
      <div className="bg-brand-teal/10 border border-brand-teal/30 rounded-lg p-6 text-center mb-8 no-print">
        <div className="text-3xl mb-2 text-brand-teal">&#10003;</div>
        <h1 className="text-2xl font-medium text-brand-dark mb-2">Booking Confirmed</h1>
        <p className="text-brand-muted">
          Nordic PO: <strong className="text-lg font-mono text-brand-dark">{poReference}</strong>
        </p>
      </div>

      {/* Warning: guests Mews could not register in any room */}
      {confirmation.companionWarnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-8 no-print">
          <p className="font-medium text-amber-800 text-sm">Some guests could not be registered in a room</p>
          <p className="text-sm text-amber-700 mt-1">
            The booking itself is confirmed, but Mews refused to add{' '}
            <strong>{confirmation.companionWarnings.join(', ')}</strong> to any of the rooms.
            Please contact the hotel so they can add the guest manually.
          </p>
        </div>
      )}

      {/* Printable confirmation */}
      <div ref={printRef} className="card p-6 md:p-8">
        <div className="text-center mb-6 pb-5 border-b border-brand-border/50">
          <img
            src="/images/logo-dark.png"
            alt="Kirsebærkroen"
            className="h-10 mx-auto mb-3"
          />
          <p className="text-brand-gold text-xs font-medium tracking-wide uppercase">
            Nordic Agent Portal &mdash; Booking Confirmation
          </p>
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-6">
          <Detail label="PO Reference" value={poReference} mono bold />
          <Detail label="Status" value="Confirmed" />
          <Detail label="Check-in" value={format(new Date(checkIn), 'EEEE dd MMM yyyy')} />
          <Detail label="Check-out" value={format(new Date(checkOut), 'EEEE dd MMM yyyy')} />
          <Detail label="Nights" value={nights} mono />
          <Detail label="Rooms" value={totalRooms} mono />
          <Detail label="Travel Agency" value="Nordic Info BV" />
          <Detail label="Booked by" value={confirmation.bookedBy || booker?.name || '—'} />
          <Detail label="Total" value={`${Math.round(totalPrice).toLocaleString('da-DK')} ${currency}`} mono bold />
        </div>

        {/* Main Guest */}
        <div className="mb-5">
          <h3 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">Main Guest</h3>
          <p className="text-sm text-brand-dark">
            {mainGuest.firstName} {mainGuest.lastName}
            {mainGuest.phone && <span className="text-brand-muted"> &middot; {mainGuest.phone}</span>}
            {mainGuest.nationalityCode && <span className="text-brand-muted"> &middot; {mainGuest.nationalityCode}</span>}
          </p>
        </div>

        {/* Room Lines */}
        <div className="mb-5">
          <h3 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">Rooms</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-brand-muted border-b border-brand-border/50">
                <th className="pb-2 font-medium">Room Type</th>
                <th className="pb-2 font-medium">Qty</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium text-right">Price ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {roomLines.map((line, i) => (
                <tr key={i} className="border-b border-brand-border/30">
                  <td className="py-2 text-brand-dark">{line.categoryName}</td>
                  <td className="py-2 font-mono">{line.quantity}</td>
                  <td className="py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-pill ${
                      line.flag === 'ALLOTMENT'
                        ? 'bg-brand-teal/10 text-brand-teal'
                        : 'bg-brand-gold/10 text-brand-gold-dark'
                    }`}>
                      {line.flag}
                    </span>
                  </td>
                  <td className="py-2 text-right font-mono">{Math.round(line.totalPrice * line.quantity).toLocaleString('da-DK')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Guest List */}
        {guests.length > 0 && (
          <div className="mb-5">
            <h3 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">Guests</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brand-muted border-b border-brand-border/50">
                  <th className="pb-2 font-medium">Name</th>
                  <th className="pb-2 font-medium">Birth Date</th>
                  <th className="pb-2 font-medium">Type</th>
                </tr>
              </thead>
              <tbody>
                {guests.map((g, i) => (
                  <tr key={i} className="border-b border-brand-border/30">
                    <td className="py-2 text-brand-dark">{g.firstName} {g.lastName}</td>
                    <td className="py-2 font-mono text-brand-muted">{g.birthDate || '—'}</td>
                    <td className="py-2">
                      <span className={`text-xs ${g.isChild ? 'text-brand-gold' : 'text-brand-muted'}`}>
                        {g.isChild ? 'Child' : 'Adult'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Breakfast */}
        <div className="mb-5">
          <h3 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">Breakfast</h3>
          <p className="text-sm text-brand-dark">
            Included in the Nordic rate for all guests.
          </p>
        </div>

        {/* Mews Reservation IDs */}
        {mewsReservations && mewsReservations.length > 0 && (
          <div className="mb-5">
            <h3 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">Mews References</h3>
            <div className="text-xs text-brand-muted space-y-1 font-mono">
              {mewsReservations.map((r, i) => (
                <p key={i}>
                  #{r.Number || i + 1}: {r.Id}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-xs text-brand-muted mt-6 pt-4 border-t border-brand-border/50">
          Booked via Nordic Agent Portal &middot; {format(new Date(), 'dd MMM yyyy HH:mm')}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between mt-6 no-print">
        <button onClick={handlePrint} className="btn-outline">
          Print Confirmation
        </button>
        <button onClick={handleNewBooking} className="btn-gold">
          New Booking
        </button>
      </div>
    </div>
  );
};

const Detail = ({ label, value, bold, mono }) => (
  <div>
    <p className="text-xs text-brand-muted uppercase tracking-wide">{label}</p>
    <p className={`text-sm text-brand-dark ${bold ? 'font-medium' : ''} ${mono ? 'font-mono' : ''}`}>{value}</p>
  </div>
);

export default Confirmation;
